/**
 * Dependency graph surface — spec §5.6.
 *
 * Route: `/graph`. Scope with `?city=<slug>`, `?open=0|1`, focus a ticket with
 * `?focus=<KEY>`, force the drawing mode with `?mode=graph|tree`. All state
 * lives in the URL so a particular view ("what is blocking Los Altos Hills") is
 * a link an agent or a teammate can be handed.
 */
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import DependencyGraph from '../../components/graph/DependencyGraph.jsx'
import '../../components/graph/graph.css'
import { Card, Placeholder } from '../city/parts.jsx'
import { CLOSED_STATUSES, apiGet, qs, useAsync } from '../city/shared.js'

export default function GraphPage() {
  const [params, setParams] = useSearchParams()
  const city = params.get('city') || ''
  const openOnly = params.get('open') !== '0'
  const focus = params.get('focus') || ''
  const mode = params.get('mode') || 'auto'
  const [resolvedMode, setResolvedMode] = useState('graph')

  const cities = useAsync(() => apiGet('/cities'), [])
  const graph = useAsync(
    () => apiGet(`/graph${qs({ city: city || undefined, open_only: openOnly })}`),
    [city, openOnly],
  )

  const setParam = (key, value) => {
    const next = new URLSearchParams(params)
    if (value === null || value === '' || value === undefined) next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
  }

  const nodes = graph.data?.nodes || []
  const edges = graph.data?.edges || []
  const blockedOpen = nodes.filter(
    (n) => !CLOSED_STATUSES.has(n.status) && edges.some((e) => e.type === 'blocks' && e.target === n.id),
  ).length

  return (
    <div className="pm-page">
      <header className="pm-page-head">
        <div>
          <h1 className="pm-title">Dependency graph</h1>
          <p className="pm-sub">
            {nodes.length} tickets · {edges.length} structural links · {blockedOpen} open ticket
            {blockedOpen === 1 ? '' : 's'} with an inbound blocker
            {city ? ` · scoped to ${city}` : ''}
          </p>
        </div>
        {city ? (
          <Link className="btn" to={`/city/${city}`}>
            City page →
          </Link>
        ) : null}
      </header>

      <div className="pm-toolbar">
        <select
          className="select"
          value={city}
          onChange={(e) => setParam('city', e.target.value)}
          title="Scope the graph to one city (blockers outside the city are still shown)"
        >
          <option value="">All cities</option>
          {(cities.data || []).map((c) => (
            <option key={c.id} value={c.slug}>
              {c.name}
            </option>
          ))}
        </select>
        <label className="pm-check">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(e) => setParam('open', e.target.checked ? null : '0')}
          />
          open work only
        </label>
        <input
          className="input"
          placeholder="Focus ticket key, e.g. LAH-14"
          value={focus}
          onChange={(e) => setParam('focus', e.target.value.toUpperCase())}
          style={{ width: 190 }}
        />
        <div className="pm-seg" title="Layered DAG, or the grouped-tree fallback">
          {['auto', 'graph', 'tree'].map((m) => (
            <button
              key={m}
              type="button"
              className="btn"
              aria-pressed={mode === m}
              onClick={() => setParam('mode', m === 'auto' ? null : m)}
            >
              {m}
            </button>
          ))}
        </div>
        <span className="pm-sub">
          rendering as <strong>{resolvedMode}</strong> · drag to pan, wheel to zoom, click a node to
          open it
        </span>
      </div>

      <Card bodyClass="">
        <Placeholder loading={graph.loading} error={graph.error} />
        {graph.data ? (
          <DependencyGraph
            nodes={nodes}
            edges={edges}
            height={640}
            focusKey={focus || null}
            mode={mode}
            onModeChange={setResolvedMode}
          />
        ) : null}
      </Card>

      <p className="pm-sub">
        Solid arrows read <strong>blocks</strong> (tail blocks head); dashed arrows run from an epic
        to its child. <code>relates_to</code> is not drawn — it carries no ordering. Cyclic
        dependencies are drawn with one edge reversed and labelled in its tooltip.
      </p>
    </div>
  )
}
